import React, { useCallback, useMemo } from 'react';

import { Table } from 'ui/Table';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { formatDateDisplay } from 'utils';
import { ESortOrder } from 'store/common/types';
import classnames from 'classnames';
import { edit } from 'ui/Icons';
import { Tooltip } from 'containers/TravelAgents/List/Tooltip';
import { CircleIconButton } from 'ui/CircleIconButton';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { useHistory } from 'react-router';
import {
  EBookingActivityStatus,
  ECrmDirectoryEntryType,
  ECrmDirectoryListSortableKey,
  IDirectoryEntrySearchResult,
} from 'services/CrmApi/types/DirectoryEntry';
import CalendarCheckIcon from 'ui/Icons/tabler/calendar-check.component.svg';
import CalendarTimeIcon from 'ui/Icons/tabler/calendar-time.component.svg';
import HourglassEmptyIcon from 'ui/Icons/tabler/hourglass-empty.component.svg';
import CalendarXIcon from 'ui/Icons/tabler/calendar-x.component.svg';
import { useResponsive } from '../hooks/useResponsive';
import { ContactIcon } from 'ui/Icons/components/CrmContact.component';
import { theme } from '../../../../tailwind.config';
import { CompanyIcon } from 'ui/Icons/components/Company.component';
import { ProgressBar } from 'ui/ProgressBar';
import { Rating } from 'react-simple-star-rating';
import Flag from 'react-world-flags';
import { useSorting } from 'hooks/useSorting';

const directoryEntryTypeMapping = {
  [ECrmDirectoryEntryType.PERSON]: <ContactIcon fill={theme.colors['gray-60']} />,
  [ECrmDirectoryEntryType.COMPANY]: <CompanyIcon fill={theme.colors['gray-60']} />,
};

const BOOKING_ACTIVITY_CONFIG: Record<
  EBookingActivityStatus,
  { Icon: React.FC<React.SVGProps<SVGSVGElement>>; colorClass: string }
> = {
  [EBookingActivityStatus.ACTIVE]: { Icon: CalendarCheckIcon, colorClass: 'text-status-confirmed' },
  [EBookingActivityStatus.INACTIVE]: { Icon: CalendarTimeIcon, colorClass: 'text-status-requested' },
  [EBookingActivityStatus.NOT_CONFIRMED]: { Icon: HourglassEmptyIcon, colorClass: 'text-status-enquiry' },
  [EBookingActivityStatus.NO_BOOKINGS]: { Icon: CalendarXIcon, colorClass: 'text-status-discarded' },
};

const formatActivityDate = (dateStr: string | null): string | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const day = date.getDate();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `Last booking: ${day} ${month} ${year}`;
};

const getActivityTooltipDate = (entry: IDirectoryEntrySearchResult): string | null => {
  if (!entry.bookingActivityStatus) return null;
  if (
    entry.bookingActivityStatus === EBookingActivityStatus.ACTIVE ||
    entry.bookingActivityStatus === EBookingActivityStatus.INACTIVE
  ) {
    return formatActivityDate(entry.lastBookingConfirmedDate);
  }
  if (entry.bookingActivityStatus === EBookingActivityStatus.NOT_CONFIRMED) {
    return formatActivityDate(entry.lastBookingRequestedDate);
  }
  return null;
};

export interface ICrmDirectoryTableProps {
  directoryEntries: IDirectoryEntrySearchResult[];
  isPending: boolean;
  isFetching: boolean;
  sortBy: ECrmDirectoryListSortableKey;
  sortOrder: ESortOrder;
  onSort: (sortBy: ECrmDirectoryListSortableKey, sortOrder: ESortOrder) => void;
}

export const CrmDirectoryTable: React.FC<ICrmDirectoryTableProps> = ({
  directoryEntries,
  isPending,
  isFetching,
  sortBy,
  sortOrder,
  onSort,
}) => {
  const history = useHistory();
  const { isMobile, isTablet } = useResponsive();

  const handleEditDirectoryEntry = useCallback(
    (directoryEntryId: string, directoryEntryType: ECrmDirectoryEntryType) => {
      directoryEntryType === ECrmDirectoryEntryType.COMPANY
        ? history.push(`/crm/company/${directoryEntryId}/edit`)
        : history.push(`/crm/contact/${directoryEntryId}/edit`);
    },
    [history]
  );

  const columns: ITableColumn[] = useMemo(() => {
    return [
      {
        id: 'type',
        label: 'TYPE',
        width: isMobile ? '50px' : '75px',
        className: 'text-ellipsis overflow-hidden',
        fieldToSortBy: ECrmDirectoryListSortableKey.PROFILE_TYPE,
      },
      {
        id: 'company',
        label: 'COMPANY NAME',
        width: isMobile ? '60px' : '100px',
        fieldToSortBy: ECrmDirectoryListSortableKey.NAME,
      },
      {
        id: 'contact',
        label: 'CONTACT NAME',
        width: isMobile ? '40px' : '80px',
        fieldToSortBy: ECrmDirectoryListSortableKey.NAME,
      },
      {
        id: 'contactEmail',
        label: 'CONTACT EMAIL',
        width: isMobile ? '50px' : '100px',
        fieldToSortBy: ECrmDirectoryListSortableKey.NAME,
        isHidden: isMobile || isTablet,
      },
      {
        id: 'manager',
        label: 'MANAGER',
        width: '80px',
        fieldToSortBy: ECrmDirectoryListSortableKey.MANAGER,
        isHidden: isMobile || isTablet,
      },
      {
        id: 'country',
        label: 'COUNTRY',
        width: '20px',
        fieldToSortBy: ECrmDirectoryListSortableKey.COUNTRY,
        isHidden: isMobile || isTablet,
      },
      {
        id: 'stage',
        label: 'STAGE',
        width: isMobile ? '15px' : '35px',
        fieldToSortBy: ECrmDirectoryListSortableKey.STAGE,
        className: 'pl-[7px]',
        isHidden: isMobile || isTablet,
      },
      {
        id: 'score',
        label: 'SCORE',
        width: isMobile ? '25px' : '30px',
        className: 'pl-[16px]',
        fieldToSortBy: ECrmDirectoryListSortableKey.SCORE,
        isHidden: isMobile || isTablet,
      },
      {
        id: 'created',
        label: 'CREATED',
        width: '25px',
        fieldToSortBy: ECrmDirectoryListSortableKey.CREATED_AT,
        isHidden: isMobile || isTablet,
      },
      {
        id: 'activity',
        label: '',
        width: '20px',
      },
      { id: 'actions', label: '', width: '20px', isHidden: isMobile },
    ];
  }, [isMobile, isTablet]);

  const rows: ITableRow[] = useMemo(() => {
    if (!directoryEntries) {
      return [];
    }
    return directoryEntries.map(directoryEntry => {
      const isCompany = directoryEntry.directoryEntryType === ECrmDirectoryEntryType.COMPANY;
      const isContact = directoryEntry.directoryEntryType === ECrmDirectoryEntryType.PERSON;
      const firstLinkedDirectoryEntry = directoryEntry.linkedDirectoryEntries?.[0];
      const firstLinkedDirectoryEntryName = firstLinkedDirectoryEntry?.fullName ?? '';
      const linkedCompaniesCount = directoryEntry.linkedDirectoryEntries.filter(
        directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.COMPANY
      ).length;
      const hasMultipleLinkedCompanies = linkedCompaniesCount > 1;
      const linkedContactsCount = directoryEntry.linkedDirectoryEntries.filter(
        directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.PERSON
      ).length;
      const hasMultipleLinkedContact = linkedContactsCount > 1;
      const editDirectoryEntry = () => {
        if (isMobile) {
          handleEditDirectoryEntry(directoryEntry.id, directoryEntry.directoryEntryType);
        }
      };

      const companyNameValue = isCompany ? directoryEntry.name : firstLinkedDirectoryEntryName;
      const companyName = (
        <div className={classnames('flex items-center gap-[10px]')} onClick={editDirectoryEntry}>
          <span>{companyNameValue}</span>
          {isContact && hasMultipleLinkedCompanies && (
            <span className="text-13px text-brown-prime flex flex-col text-center">
              <span>{linkedCompaniesCount - 1}</span>
              <span className="break-keep">More</span>
            </span>
          )}
        </div>
      );
      const contactNameValue = isContact ? directoryEntry.name : firstLinkedDirectoryEntryName;
      const contactName = (
        <div className={classnames('flex justify-between items-center gap-[10px]')} onClick={editDirectoryEntry}>
          <span>{contactNameValue}</span>
          {isCompany && hasMultipleLinkedContact && (
            <span className="text-13px text-brown-prime flex flex-col text-center">
              <span>{linkedContactsCount - 1}</span>
              <span className="break-keep">More</span>
            </span>
          )}
        </div>
      );
      const contactEmailValue = isContact ? directoryEntry.email : firstLinkedDirectoryEntry?.email ?? '';
      const contactEmail = (
        <div className={classnames('flex items-center gap-[10px]')} onClick={editDirectoryEntry}>
          <span className="">{contactEmailValue}</span>
        </div>
      );
      const type = (
        <div className={classnames('flex items-center gap-[10px]')} onClick={editDirectoryEntry}>
          {directoryEntryTypeMapping[directoryEntry.directoryEntryType]}
          <span className="">{directoryEntry.profileType.value}</span>
        </div>
      );
      const managerName = [directoryEntry.manager.firstName, directoryEntry.manager.lastName].filter(Boolean).join(' ');
      const country = directoryEntry.country ? (
        <span onClick={editDirectoryEntry}>
          <Flag code={directoryEntry.country.code} style={{ width: 18, height: 18 }} />
        </span>
      ) : null;
      const stage = (
        <div className="w-[50px]" onClick={editDirectoryEntry}>
          <ProgressBar score={directoryEntry.leadStage?.order ?? 0} total={4} />
        </div>
      );
      const score = (
        <span onClick={editDirectoryEntry}>
          {directoryEntry.leadScore !== null && (
            <Rating size={14} initialValue={directoryEntry.leadScore ?? undefined} transition readonly />
          )}
        </span>
      );
      const actions = (
        <div className="item-actions flex items-center relative">
          <div className="w-4px h-34px rounded bg-brown-15 mr-10px" />
          <Tooltip content="Edit">
            <CircleIconButton
              type="secondary"
              className="edit-action mx-1"
              iconClass="inline relative left-0 top-1px w-12px h-12px"
              iconSrc={edit}
              onClick={() => handleEditDirectoryEntry(directoryEntry.id, directoryEntry.directoryEntryType)}
            />
          </Tooltip>
        </div>
      );
      return {
        id: directoryEntry.profileId,
        cells: [
          { id: 'type', value: type, title: directoryEntry.profileType.value, className: '' },
          { id: 'company', value: companyName, title: companyNameValue, className: 'break-all' },
          { id: 'contact', value: contactName, title: contactNameValue, className: 'break-all' },
          {
            id: 'contactEmail',
            value: contactEmail,
            title: contactEmailValue ?? undefined,
            className: 'break-all',
            isHidden: isMobile || isTablet,
          },
          { id: 'manager', value: managerName, title: managerName, isHidden: isMobile || isTablet },
          {
            id: 'country',
            value: country,
            title: directoryEntry.country?.name ?? '',
            className: 'pl-[24px]',
            isHidden: isMobile || isTablet,
          },
          {
            id: 'stage',
            value: stage,
            title: directoryEntry.leadStage?.value ?? '',
            className: '',
            isHidden: isMobile || isTablet,
          },
          {
            id: 'score',
            value: score,
            title: String(directoryEntry.leadScore),
            className: '',
            isHidden: isMobile || isTablet,
          },
          {
            id: 'created',
            value: formatDateDisplay(directoryEntry.createdAt),
            title: formatDateDisplay(directoryEntry.createdAt),
            isHidden: isMobile || isTablet,
          },
          {
            id: 'activity',
            value: (() => {
              if (!directoryEntry.bookingActivityStatus) return null;
              const config = BOOKING_ACTIVITY_CONFIG[directoryEntry.bookingActivityStatus];
              const { Icon, colorClass } = config;
              const tooltipText = getActivityTooltipDate(directoryEntry);
              const icon = <Icon className={classnames('w-[20px] h-[20px]', colorClass)} />;
              if (tooltipText) {
                return <Tooltip content={tooltipText}>{icon}</Tooltip>;
              }
              return icon;
            })(),
          },
          { id: 'actions', value: actions, isHidden: isMobile },
        ],
      };
    });
  }, [directoryEntries, isMobile, isTablet, handleEditDirectoryEntry]);

  if (isPending || !directoryEntries) {
    return (
      <div className="mt-[20px]">
        <LoadingBar />
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      rows={rows}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      messageWhenEmpty="No results"
      evenColor="ivory"
      loading={isFetching}
    />
  );
};
